import {
  Box,
  Button,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Theme,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import axios from 'axios';
import clsx from 'clsx';
import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../../shared/context/AuthContext';
import { ConferenceContext } from '../../../shared/context/ConferenceContext';
import { DiscussionTopicEdit } from './DiscussionTopicEdit';
import C from '../../../../../shared/constants/general';

const useStyles = makeStyles((theme: Theme) => ({
  holder: {
    // minHeight: 150,
  },
  expanded: {
    backgroundColor: theme.palette.background.shadedown_20,
    borderRadius: 5,
  },
  listRoot: {
    display: 'flex',
    flexDirection: 'column',
  },
  listItemRoot: {
    opacity: 1,
    '&:hover': {
      // opacity: 1,
      color: theme.palette.primary.main,
    },
  },
  listItemSelected: {
    opacity: 1,
  },
  listItemEditing: {
    padding: 0,
    '&:hover': {
      // opacity: 1,
      color: theme.palette.text.primary,
    },
  },
  topicEdit: {
    textTransform: 'uppercase',
    width: '100%',
  },
  listText: {
    // width: '100%',
    // display: 'flex',
    // alignItems: 'center',
    color: theme.palette.text.primary,
    textTransform: 'uppercase',
    fontWeight: theme.typography.fontWeightBold,
    textDecoration: 'none',
  },
  listIcon: {
    fontSize: '1rem',
    lineHeight: '1.3rem',
  },
  topicHidden: {
    opacity: 0.7,
  },
}));

interface IProps {
  minimize: boolean;
  // menuItems: any[];
  topicSlug: string;
  topics: any[];
  setTopics: Dispatch<SetStateAction<any[]>>;
  refresh: () => void;
}

const postStateIcon: { [key: string]: string } = {
  open: 'tag',
  suggest: 'verified',
  closed: 'lock',
};

const DiscussionTopics = ({
  minimize = false,
  topicSlug = '',
  topics,
  setTopics,
  refresh,
}: IProps) => {
  const classes = useStyles();
  const topicModalRef = useRef();
  const [editing, setEditing] = useState(false);
  const [expand, setExpand] = useState(false);
  const [postsWithoutTopic, setPostsWithoutTopic] = useState(0);

  const { authState } = useContext(AuthContext);
  const isModerator =
    authState.user.access_level === C.VISITOR.ACCESS_LEVEL.MODERATOR;

  useEffect(() => {
    isModerator &&
      axios
        .get(
          `/api/visitor/conferences/${
            (window as any).CONFERENCE_ID
          }/unconnecteddiscussions`
        )
        .then((response) => {
          setPostsWithoutTopic(response.data.count);
        });
  }, []);

  const saveSort = () => {
    axios
      .put(
        `/api/visitor/conferences/${(window as any).CONFERENCE_ID}/sorttopics`,
        {
          topicIds: topics.map((t) => t.id),
        }
      )
      .then((response) => {
        refresh();
      });
  };

  const toggleEditing = () => {
    if (editing) {
      saveSort();
    }
    setEditing(!editing);
  };

  const sortTopicUp = (index: number) => {
    const newTopics = [...topics];
    newTopics.splice(index - 1, 0, newTopics.splice(index, 1)[0]);
    setTopics(newTopics);
  };

  const openNewTopic = () => {
    topicModalRef.current.open({
      topic: {
        title: '',
        slug: '',
        poststate: 'open',
        description: '',
        ai: false,
      },
      callback: refresh,
      postsWithoutTopic,
    });
  };
  const editTopic = (topic: any) => {
    topicModalRef.current.open({
      topic: topic,
      callback: refresh,
      postsWithoutTopic,
    });
  };

  return (
    <Box
      className={clsx(classes.holder, {
        [classes.expanded]: expand,
      })}
    >
      {minimize && (
        <Box textAlign="center">
          <Button
            startIcon={<Icon>{expand ? 'expand_less' : 'expand'}</Icon>}
            size="small"
            onClick={() => setExpand(!expand)}
          >
            {topicSlug ? `# ${topicSlug}` : 'all'}
          </Button>
        </Box>
      )}

      {(!minimize || expand) && (
        <>
          <List classes={{ root: classes.listRoot }}>
            <ListItem
              classes={{
                root: classes.listItemRoot,
                selected: classes.listItemSelected,
              }}
              selected={!topicSlug && !editing}
            >
              <Link
                className={classes.listText}
                to={{ pathname: '/live/discussion' }}
              >
                All
              </Link>
            </ListItem>
            {topics.map((topic, index) => (
              <ListItem
                key={topic.id}
                classes={{
                  root: classes.listItemRoot,
                  selected: classes.listItemSelected,
                }}
                className={clsx({
                  [classes.listItemEditing]: editing,
                })}
                selected={topicSlug === topic.slug && !editing}
              >
                {editing ? (
                  <Box
                    display="flex"
                    className={classes.topicEdit}
                    alignItems="center"
                  >
                    <IconButton
                      size="small"
                      disabled={index === 0}
                      onClick={() => sortTopicUp(index)}
                    >
                      <Icon>arrow_upward</Icon>
                    </IconButton>
                    <Box flexGrow={1}>{topic.title}</Box>
                    <IconButton size="small" onClick={() => editTopic(topic)}>
                      <Icon>edit</Icon>
                    </IconButton>
                  </Box>
                ) : (
                  <Link
                    className={clsx(classes.listText, {
                      [classes.topicHidden]: topic.viewstate === 'moderators',
                    })}
                    to={{ pathname: `/live/discussion/${topic.slug}` }}
                  >
                    <Icon className={classes.listIcon}>
                      {postStateIcon[topic.poststate]}
                    </Icon>{' '}
                    {topic.title} ({topic.discussion_count})
                  </Link>
                )}
              </ListItem>
            ))}
            {!editing && (
              <ListItem
                classes={{
                  root: classes.listItemRoot,
                  selected: classes.listItemSelected,
                }}
                selected={topicSlug === 'my'}
              >
                <Link
                  className={classes.listText}
                  to={{ pathname: `/live/discussion/my` }}
                >
                  my posts
                </Link>
              </ListItem>
            )}
          </List>

          {editing && (
            <Button
              size="small"
              onClick={openNewTopic}
              startIcon={<Icon>add_circle</Icon>}
              color="primary"
            >
              New topic
            </Button>
          )}

          {isModerator && (
            <Box p={1}>
              <Button
                size="small"
                onClick={toggleEditing}
                startIcon={<Icon>edit</Icon>}
                color={editing ? 'primary' : 'secondary'}
              >
                {editing ? 'Stop editing' : 'Edit topics'}
              </Button>
            </Box>
          )}

          {isModerator && postsWithoutTopic > 0 && (
            <Box p={1}>
              <Typography color="error">
                There are {postsWithoutTopic} posts not connected to any topic.
              </Typography>
            </Box>
          )}
        </>
      )}

      <DiscussionTopicEdit ref={topicModalRef} />
    </Box>
  );
};

export default DiscussionTopics;
